// src/pages/About.jsx
import React from 'react';
import { Paper, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import depotImage from '../assets/about/depot.png';
import about1 from '../assets/about/about1.JPG';
import about2 from '../assets/about/about2.jpg';
import about3 from '../assets/about/about3.JPG';

export default function About() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const MOBILE_IMG_BOX_SX = {
        width: '100%',
        aspectRatio: '4 / 3',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: 1,
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
        },
    };

    const MobileCarousel = (
        <Box sx={{ mt: 2 }}>
            <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                style={{ paddingBottom: theme.spacing(1) }}
            >
                {[about1, about2, about3].map((src, i) => (
                    <SwiperSlide key={i}>
                        <Box sx={MOBILE_IMG_BOX_SX}>
                            <img src={src} alt={`Museum photo ${i + 1}`} loading="lazy" decoding="async" />
                        </Box>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    );

    return (
        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'flex-start',
                    gap: { xs: 3, sm: 4, md: 4 },
                }}
            >
                {/* LEFT — Depot + (desktop-only) stacked photos */}
                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 40%' }, textAlign: 'center' }}>
                    <Box
                        component="img"
                        src={depotImage}
                        alt="Piedmont Southern Railroad Depot Museum"
                        loading="lazy"
                        decoding="async"
                        sx={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 1.5,
                            objectFit: 'cover',
                            boxShadow: { xs: 1, md: 0 },
                        }}
                    />

                    {/* IMPORTANT CHANGE:
              We removed the mobile slider from here so there’s no big, unscrollable gray area.
              On desktop we keep the stacked photos exactly as before. */}
                    <Box
                        sx={{
                            mt: 2,
                            display: { xs: 'none', md: 'grid' },
                            gridTemplateColumns: '1fr',
                            gap: 2,
                        }}
                    >
                        <Box
                            component="img"
                            src={about1}
                            alt="Historical artifacts in the depot"
                            loading="lazy"
                            decoding="async"
                            sx={{ width: '100%', borderRadius: 1, objectFit: 'cover' }}
                        />
                        <Box
                            component="img"
                            src={about2}
                            alt="Museum interior exhibit"
                            loading="lazy"
                            decoding="async"
                            sx={{ width: '100%', borderRadius: 1, objectFit: 'cover' }}
                        />
                        <Box
                            component="img"
                            src={about3}
                            alt="Visitors exploring exhibits"
                            loading="lazy"
                            decoding="async"
                            sx={{ width: '100%', borderRadius: 1, objectFit: 'cover' }}
                        />
                    </Box>
                </Box>

                {/* RIGHT — Mission Statement */}
                <Box sx={{ flex: 1, maxWidth: { md: '65ch' } }}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.6rem', sm: '1.8rem', md: undefined },
                            lineHeight: { xs: 1.25, sm: 1.25, md: undefined },
                        }}
                    >
                        Mission Statement
                    </Typography>

                    <Typography
                        paragraph
                        sx={{ fontSize: { xs: '1rem', sm: '1.025rem' }, lineHeight: { xs: 1.7, md: 1.75 } }}
                    >
                        The mission of the Piedmont Historical Society is to collect, preserve, and communicate
                        the history of Piedmont, Calhoun County, and the State of Alabama for the educational
                        benefit of all people. This mission guides our development and operation of high-quality
                        historical and cultural educational programs. The Piedmont Southern Railroad Depot Museum
                        is an 1868 railroad station listed on the National Register of Historic Places. The depot
                        has been restored to its nostalgic era and modernized through a complete renovation of its
                        interior and exterior into an environmentally safe, comfortable, and energy-efficient public
                        museum facility.
                    </Typography>

                    <Typography
                        paragraph
                        sx={{ fontSize: { xs: '1rem', sm: '1.025rem' }, lineHeight: { xs: 1.7, md: 1.75 } }}
                    >
                        Our primary audience is the more than 77,000 fourth-grade students in Alabama’s public and
                        private schools who study Alabama history. Our objective is to provide a low-cost, high-quality
                        educational field trip venue for all Alabama students and teachers to reinforce their knowledge
                        of the state’s rich history and culture each year.
                    </Typography>

                    <Typography
                        paragraph
                        sx={{ fontSize: { xs: '1rem', sm: '1.025rem' }, lineHeight: { xs: 1.7, md: 1.75 } }}
                    >
                        We offer interactive displays such as the Alabama Humanities Foundation’s MAKING ALABAMA 2019
                        Bicentennial Exhibit which is a valuable, eight-station computer terminal display of Alabama
                        history housed in the depot’s cargo room. At the center of the cargo room stands a unique
                        7.5×9-foot white-screened topographical map of Alabama for projection of audiovisual presentations.
                        This raised map allows visitors, including those with visual impairments, to feel the mountains
                        of northeast Alabama, the Black Belt coastal plains, and the Gulf Coast river systems and valleys.
                    </Typography>

                    <Typography
                        paragraph
                        sx={{ fontSize: { xs: '1rem', sm: '1.025rem' }, lineHeight: { xs: 1.7, md: 1.75 } }}
                    >
                        Students receive an 1870s Southern Railroad train ticket as a memento and embark on a scavenger
                        hunt to interact with the exhibits and develop dexterity skills by labeling a paper copy of the
                        Alabama map. Their “Piedmont Express” journey concludes with refreshments and a cookie shaped like
                        Alabama, where they mark their hometown before enjoying a snack. The depot deck provides a large
                        outdoor space with tables, benches, and umbrellas for students to review their answers and discuss
                        their learning experience.
                    </Typography>

                    <Typography
                        paragraph
                        sx={{ fontSize: { xs: '1rem', sm: '1.025rem' }, lineHeight: { xs: 1.7, md: 1.75 } }}
                    >
                        The Piedmont Southern Railroad Depot Museum, Caboose Museum, and Roberts Home Museum welcome visitors
                        of all ages. Our museums are accessible and inclusive, ensuring that no one is left behind.
                    </Typography>

                    <Typography
                        paragraph
                        sx={{ fontSize: { xs: '1rem', sm: '1.025rem' }, lineHeight: { xs: 1.7, md: 1.75 } }}
                    >
                        The Piedmont Historical Society sponsors educational history programs such as book signings,
                        author meet-and-greets, traveling exhibits, guest speakers, and cemetery strolls. Come and visit us!
                    </Typography>
                </Box>
            </Box>

            {/* MOBILE-ONLY: move the photos to the very bottom of the page */}
            {isMobile && <Box sx={{ display: { xs: 'block', md: 'none' } }}>{MobileCarousel}</Box>}
        </Paper>
    );
}
